// src/components/common/FloatingToast.jsx
//
// Fixed-position toast notification. Mounts at top-center of the viewport,
// floats over everything (high z-index), does NOT take space in document
// flow — so showing/hiding it never shifts the underlying UI down or back.
//
// Pattern mirrors the established success/warning toasts in
// src/share/sharingCenter.jsx (top-6 left-1/2 -translate-x-1/2) so the
// app's toast UX feels consistent across surfaces. Extracted to a shared
// component so future surfaces can reuse it without copy-pasting Tailwind
// classes.
//
// Replaces the old inline-banner pattern in OngoingProfileAssistant where
// success messages rendered inside the section's normal flow, pushing
// every element below downward (Brett: "the entire section shifts
// downward... I don't want the UI to move"). Floating means zero layout
// reflow.
//
// Usage:
//   const [toast, setToast] = useState(null);  // { text, type } or null
//   <FloatingToast toast={toast} onDismiss={() => setToast(null)} />
//
//   // Show success: setToast({ text: 'Saved successfully!', type: 'success' });
//   // Show error:   setToast({ text: 'Upload failed', type: 'error' });
//   // Show info:    setToast({ text: 'Uploading...', type: 'info' });
//
// Auto-dismiss:
//   Pass autoDismissMs (default 2500ms for success/info, 4500ms for error).
//   Pass autoDismissMs={0} or null to disable auto-dismiss (manual dismiss only).
//
// Types -> palette:
//   success -> green
//   error   -> red
//   info    -> blue (used for "Uploading...", in-progress states)

import React, { useEffect } from 'react';

const TYPE_STYLES = {
  success: {
    bg: 'bg-green-50',
    border: 'border-green-300',
    text: 'text-green-800',
    icon: '✓',
    iconClass: 'text-green-600',
    dismissClass: 'text-green-400 hover:text-green-600',
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-300',
    text: 'text-red-800',
    icon: '✕',
    iconClass: 'text-red-600',
    dismissClass: 'text-red-400 hover:text-red-600',
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-300',
    text: 'text-blue-800',
    icon: 'ⓘ',
    iconClass: 'text-blue-600',
    dismissClass: 'text-blue-400 hover:text-blue-600',
  },
};

export default function FloatingToast({
  toast,           // { text, type } or null
  onDismiss,
  autoDismissMs,   // override default. Pass 0/null to disable.
  zIndex = 9999,   // override if the host UI uses a higher stack
}) {
  // Auto-dismiss timer — restarts whenever toast changes
  useEffect(() => {
    if (!toast) return;
    // Default dismiss timing: errors stay visible longer because users
    // need time to actually read them. Success/info dismiss faster.
    const defaultMs = toast.type === 'error' ? 4500 : 2500;
    const ms = autoDismissMs === 0 || autoDismissMs === null ? null : (autoDismissMs ?? defaultMs);
    if (!ms) return;
    const t = setTimeout(() => onDismiss?.(), ms);
    return () => clearTimeout(t);
  }, [toast, autoDismissMs, onDismiss]);

  if (!toast) return null;
  const type = toast.type || 'success';
  const styles = TYPE_STYLES[type] || TYPE_STYLES.success;

  return (
    <div
      className={`pointer-events-auto fixed top-6 left-1/2 -translate-x-1/2 flex items-center gap-2 px-5 py-3 rounded-lg shadow-lg ${styles.bg} ${styles.border} border animate-[fadeIn_0.2s_ease-out]`}
      style={{ zIndex }}
      role="status"
      aria-live="polite"
    >
      <span className={`text-lg ${styles.iconClass}`}>{styles.icon}</span>
      <span className={`text-sm font-medium ${styles.text}`}>{toast.text}</span>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className={`ml-2 text-lg leading-none ${styles.dismissClass}`}
          aria-label="Dismiss"
        >
          ×
        </button>
      )}
    </div>
  );
}
